import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCurrentMembership, getEventById, registerForEvent, getUserEventRegistrations, cancelEventRegistration, createEventPaymentIntent, getAssociateMembers, getCreditBalance } from '../services/api';
import sanitizeHtml from '../utils/sanitizeHtml';
import EventPaymentForm from '../components/events/EventPaymentForm';
import Alert from '../components/common/Alert';

const FORMAT_LABELS = {
  in_person: 'In-person',
  online: 'Online',
  hybrid: 'Hybrid',
};

function EventDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [membership, setMembership] = useState(null);
  const [userRegistrations, setUserRegistrations] = useState([]);
  const [associateMembers, setAssociateMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [registering, setRegistering] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    // Guard against a stale in-flight request from a previous event id
    // resolving after this one and clobbering state with the wrong event's
    // data (e.g. userRegistrations, which pricing now depends on).
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const authFetches = user
          ? [
              getCurrentMembership().catch(() => null),
              getUserEventRegistrations().catch(() => ({ results: [] })),
              getAssociateMembers().catch(() => ({ results: [] })),
            ]
          : [Promise.resolve(null), Promise.resolve({ results: [] }), Promise.resolve({ results: [] })];

        const [ev, mem, registrations, associates] = await Promise.all([
          getEventById(id),
          ...authFetches,
        ]);
        if (cancelled) return;

        setEvent(ev);
        setMembership(mem);

        // The /event-registrations/ endpoint returns every registration in
        // the system for staff/admins (so AdminEventRegistrations.jsx can
        // list all registrants), not just the caller's own - so this must
        // filter by owner too, not just event + status, or an admin's own
        // "am I registered" / family-position pricing would be computed
        // from everyone else's registrations as well as their own.
        const regs = registrations.results || registrations;
        const eventRegs = regs.filter(reg =>
          reg.event === parseInt(id) && reg.status === 'registered' && reg.user === user?.id
        );
        setUserRegistrations(eventRegs);

        const assocs = associates.results || associates;
        setAssociateMembers(assocs);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load event');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [id, user]);

  const toAmount = (value) => {
    const numeric = Number(value);
    return Number.isNaN(numeric) ? 0 : numeric;
  };

  const basePriceAmount = event ? toAmount(event.base_price) : 0;
  const hasExplicitProPrice = event ? event.pro_member_price !== null && event.pro_member_price !== undefined : false;
  const proPriceAmount = hasExplicitProPrice ? toAmount(event.pro_member_price) : basePriceAmount;

  // Early bird pricing
  const isEarlyBirdActive = event ? event.is_early_bird_active : false;
  const earlyBirdPrice = event && event.early_bird_price !== null ? toAmount(event.early_bird_price) : null;
  const earlyBirdProPrice = event && event.early_bird_pro_price !== null ? toAmount(event.early_bird_pro_price) : null;

  const formatCurrency = (amount) => (amount === 0 ? 'Free' : `$${amount.toFixed(2)}`);

  // How many registrations already exist for this account+event (primary +
  // associates combined) - the next one being registered takes this + 1.
  // Mirrors payment_views.py's family_position exactly.
  const familyPosition = userRegistrations.length + 1;

  // How many of the account's registrants get Pro pricing per event: 0 for
  // Community, defaults to 1 for Pro tiers, higher if benefits overrides it
  // (e.g. Family Pro). Mirrors MembershipType.event_pro_price_cap.
  const proPriceCap = useMemo(() => {
    const benefits = membership?.membership_type?.benefits;
    if (!benefits || benefits.access_type !== 'pro') return 0;
    return benefits.pro_price_cap ?? 1;
  }, [membership]);

  const appliesProPrice = familyPosition <= proPriceCap;

  const registrationPrice = useMemo(() => {
    if (!event) return 0;

    let price;
    if (isEarlyBirdActive && appliesProPrice && earlyBirdProPrice !== null) {
      price = earlyBirdProPrice;
    } else if (isEarlyBirdActive && earlyBirdPrice !== null) {
      price = earlyBirdPrice;
    } else if (appliesProPrice && hasExplicitProPrice) {
      price = proPriceAmount;
    } else {
      price = basePriceAmount;
    }

    // Beyond the account's Pro-price cap for this event, and not the
    // account's very first registration for it: flat 10% family discount.
    // Mirrors payment_views.py's create_event_payment_intent exactly.
    if (!appliesProPrice && familyPosition > 1) {
      price = price * 0.9;
    }

    return price;
  }, [event, appliesProPrice, familyPosition, hasExplicitProPrice, proPriceAmount, basePriceAmount, isEarlyBirdActive, earlyBirdPrice, earlyBirdProPrice]);

  // "Your price" only makes sense for the account's own/first registration
  // for this event; every registration after that is pricing a specific
  // additional family member, which can look like a broken/mismatched
  // number (e.g. 10% off Community) if it's labeled the same generic way.
  const isAdditionalFamilyMember = familyPosition > 1;
  const priceLabel = isAdditionalFamilyMember ? 'Additional Family Member Price' : 'Your Price';

  const priceExplanation = (() => {
    if (!appliesProPrice && isAdditionalFamilyMember) {
      return isEarlyBirdActive
        ? '10% family discount applied to the early bird community price for additional family members.'
        : '10% family discount applied to the community price for additional family members.';
    }
    if (isEarlyBirdActive) return 'Early bird pricing applied automatically.';
    if (hasExplicitProPrice) return 'Pro pricing automatically applies to Pro members when available.';
    return 'Registration uses the community rate.';
  })();

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit'
    }).format(date);
  };


  // /event-registrations/ returns every registration in the system for
  // staff/admins, not just the caller's own - always filter by owner too.
  const refreshUserRegistrations = async () => {
    const registrations = await getUserEventRegistrations();
    const regs = registrations.results || registrations;
    const eventRegs = regs.filter(reg =>
      reg.event === parseInt(id) && reg.status === 'registered' && reg.user === user?.id
    );
    setUserRegistrations(eventRegs);
  };

  const onBeginRegister = async () => {
    if (!event) return;

    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`/events/${id}`)}`);
      return;
    }

    setError('');

    // If event requires payment, check if credits cover the full amount
    if (registrationPrice > 0) {
      setRegistering(true);

      try {
        // Check credit balance
        const creditData = await getCreditBalance();
        const creditBalance = Number(creditData.balance || 0);
        const price = Number(registrationPrice);

        // If credits cover the full amount, register directly using payment intent endpoint
        if (creditBalance >= price) {
          const result = await createEventPaymentIntent({
            event_id: event.id,
            associate_member_id: null
          });

          // Backend will auto-register and return free_event: true when credits cover it
          if (result.free_event) {
            // Refresh registrations to show the new one
            await refreshUserRegistrations();

            // Refresh event data
            const ev = await getEventById(id);
            setEvent(ev);

            setSuccess('Successfully registered! Payment covered by your account credits.');
            setRegistering(false);
            return;
          } else {
            // Unexpected: credits should have covered it but backend didn't return free_event
            setError('Registration failed. Please try again.');
            setRegistering(false);
            return;
          }
        }

        // Credits don't cover full amount, show payment form
        setRegistering(false);
        setPaymentModalOpen(true);
      } catch (e) {
        setError(e.message || 'Failed to process registration. Please try again.');
        setRegistering(false);
      }
      return;
    }

    // Handle free event registration directly
    setRegistering(true);
    try {
      await registerForEvent({
        event: event.id
      });

      // Refresh registrations to show the new one
      await refreshUserRegistrations();

      // Refresh event data
      const ev = await getEventById(id);
      setEvent(ev);

      setSuccess('Successfully registered!');
    } catch (e) {
      setError(e.message || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const onPaymentSuccess = async (result) => {
    setPaymentModalOpen(false);

    // Refresh registrations to show the new one
    try {
      await refreshUserRegistrations();

      // Refresh event data
      const ev = await getEventById(id);
      setEvent(ev);

      setSuccess('Successfully registered! Payment confirmed.');
    } catch (err) {
      // If refresh fails, still show success and navigate
      navigate('/events', {
        replace: true,
        state: {
          success: `Successfully registered for ${event.name}! Payment confirmed.`
        }
      });
    }
  };

  const onPaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleCancelRegistration = async (registration) => {
    const pricePaid = Number(registration.price_paid || 0);
    const confirmMessage = pricePaid > 0
      ? `Are you sure you want to cancel ${registration.attendee_name}'s registration for "${event.name}"?\n\n$${pricePaid.toFixed(2)} will be added to your account as credit. If you'd like a cash refund instead, please contact support.`
      : `Are you sure you want to cancel ${registration.attendee_name}'s registration for "${event.name}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setCancelling(true);
    setError('');
    setSuccess('');

    try {
      const response = await cancelEventRegistration(registration.id);

      // Show success message with refund info
      if (response.refund) {
        setSuccess(`Successfully cancelled ${registration.attendee_name}'s registration. ${response.refund.message}`);
      } else {
        setSuccess(`Successfully cancelled ${registration.attendee_name}'s registration`);
      }

      // Remove from local state
      setUserRegistrations(prev => prev.filter(reg => reg.id !== registration.id));

      // Refresh event data to update available spots
      const ev = await getEventById(id);
      setEvent(ev);
    } catch (err) {
      setError(err.message || 'Failed to cancel registration');
    } finally {
      setCancelling(false);
    }
  };


  const format = event?.event_format || 'in_person';
  const formatLabel = FORMAT_LABELS[format] || 'In-person';
  const formatBadgeClass =
    format === 'online'
      ? 'bg-blue-100 text-blue-800'
      : format === 'hybrid'
        ? 'bg-purple-100 text-purple-800'
        : 'bg-green-100 text-green-800';
  const primaryImage = event?.images?.length ? event.images[0] : null;
  const galleryImages = event?.images?.length > 1 ? event.images.slice(1) : [];
  const sanitizedDescription = useMemo(() => sanitizeHtml(event?.description), [event?.description]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Alert type="error" message={error || 'Event not found'} onClose={() => navigate('/events')} />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <button className="text-blue-600 mb-4" onClick={() => navigate(-1)}>&larr; Back</button>

      <Alert type="error" message={error} onClose={() => setError('')} />
      <Alert type="success" message={success} onClose={() => setSuccess('')} />

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {primaryImage?.image && (
          <a href={primaryImage.image} target="_blank" rel="noopener noreferrer">
            <img
              src={primaryImage.image}
              alt={primaryImage.caption || `${event.name} featured visual`}
              className="w-full h-64 object-contain bg-gray-100"
            />
          </a>
        )}
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-2xl font-bold text-gray-900">{event.name}</h1>
            <span className={`${formatBadgeClass} text-xs px-2 py-1 rounded whitespace-nowrap`}>{formatLabel}</span>
          </div>

          {(event.speaker || event.speaker_picture) && (
            <div className="mt-6 flex items-center gap-4">
              {event.speaker_picture && (
                <img
                  src={event.speaker_picture}
                  alt={`${event.speaker || 'Event speaker'} headshot`}
                  className="w-16 h-16 rounded-full object-cover border"
                />
              )}
              <div>
                <p className="text-sm uppercase tracking-wide text-gray-500">Featured Speaker</p>
                {event.speaker && (
                  <p className="text-lg font-semibold text-gray-900">{event.speaker}</p>
                )}
              </div>
            </div>
          )}

          <div
            className="rich-text-content text-gray-700 mt-6 space-y-4"
            dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="space-y-2 text-gray-700">
              <p><span className="font-medium">Starts:</span> {formatDateTime(event.start_date)}</p>
              <p><span className="font-medium">Ends:</span> {formatDateTime(event.end_date)}</p>
              <p>
                <span className="font-medium">Location:</span>{' '}
                {format === 'online'
                  ? 'Online'
                  : event.location || 'TBD'}
              </p>
              {format === 'hybrid' && event.location && (
                <p className="text-sm text-gray-500">Hybrid event — on-site at {event.location} and streamed online.</p>
              )}
              {event.has_online_component && event.online_url && (
                <p className="text-blue-600 truncate">
                  <a href={event.online_url} target="_blank" rel="noreferrer" className="hover:underline">
                    Join link
                  </a>
                </p>
              )}
              <p className="mt-2">
                <span className="font-medium">Capacity:</span>{' '}
                {event.capacity === 0 ? 'Unlimited' : event.capacity}
                {typeof event.available_spots === 'number' && event.capacity !== 0 && (
                  <span className="ml-2 text-sm text-gray-600">({event.available_spots} spots left)</span>
                )}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Pricing</h3>
              {isEarlyBirdActive && event.early_bird_deadline && (
                <div className="mb-2 p-2 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <p className="text-xs font-semibold text-yellow-800">🎟️ Early Bird Active</p>
                  <p className="text-xs text-yellow-700">Until {formatDateTime(event.early_bird_deadline)}</p>
                </div>
              )}
              <div className="space-y-2 text-gray-700">
                {isEarlyBirdActive ? (
                  <>
                    <p>
                      <span className="font-medium">Early Bird Community:</span> {formatCurrency(earlyBirdPrice !== null ? earlyBirdPrice : basePriceAmount)}
                      {earlyBirdPrice !== null && earlyBirdPrice !== basePriceAmount && (
                        <span className="text-xs text-gray-500 line-through ml-2">{formatCurrency(basePriceAmount)}</span>
                      )}
                    </p>
                    {hasExplicitProPrice && (
                      <p>
                        <span className="font-medium">Early Bird Pro:</span> {formatCurrency(earlyBirdProPrice !== null ? earlyBirdProPrice : proPriceAmount)}
                        {earlyBirdProPrice !== null && earlyBirdProPrice !== proPriceAmount && (
                          <span className="text-xs text-gray-500 line-through ml-2">{formatCurrency(proPriceAmount)}</span>
                        )}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p><span className="font-medium">Community:</span> {formatCurrency(basePriceAmount)}</p>
                    {hasExplicitProPrice && (
                      <p><span className="font-medium">Pro Member:</span> {formatCurrency(proPriceAmount)}</p>
                    )}
                  </>
                )}
                <p>
                  <span className="font-medium">{priceLabel}:</span> {formatCurrency(registrationPrice)}
                </p>
                <p className="text-sm text-gray-600">
                  {priceExplanation}
                </p>
              </div>
            </div>
          </div>

          {galleryImages.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold text-gray-900 mb-3">Event Gallery</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {galleryImages.map(image => (
                  <img
                    key={image.id}
                    src={image.image}
                    alt={image.caption || `${event.name} gallery image`}
                    className="w-full h-32 object-cover rounded"
                  />
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            {userRegistrations.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Your Registrations</h3>
                <div className="space-y-2">
                  {userRegistrations.map((registration) => (
                    <div key={registration.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start flex-1">
                          <svg className="h-5 w-5 text-green-600 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-green-800 font-medium">
                              {registration.attendee_name}
                              {registration.is_for_associate && (
                                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                  Associate
                                </span>
                              )}
                            </p>
                            <div className="mt-1 text-sm text-green-700">
                              <p>Registration Code: {registration.registration_code}</p>
                              {registration.price_paid > 0 && (
                                <p>Amount Paid: ${Number(registration.price_paid).toFixed(2)}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        {registration.can_cancel && (
                          <button
                            onClick={() => handleCancelRegistration(registration)}
                            disabled={cancelling}
                            className="ml-4 px-3 py-1 text-sm rounded border border-red-600 text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            {cancelling ? 'Cancelling...' : 'Cancel'}
                          </button>
                        )}
                      </div>
                      {registration.can_cancel && registration.price_paid > 0 && (
                        <p className="text-xs text-gray-600 mt-2">
                          Paid events can be cancelled up to 7 days before. Refund will be added as account credits.
                        </p>
                      )}
                      {registration.can_cancel && registration.price_paid === 0 && (
                        <p className="text-xs text-gray-600 mt-2">
                          Free events can be cancelled anytime before the event starts.
                        </p>
                      )}
                      {!registration.can_cancel && registration.cancel_reason && (
                        <p className="text-xs text-gray-600 mt-2">
                          {registration.cancel_reason}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!(userRegistrations.length > 0 && associateMembers.length === 0) && (
              <button
                disabled={event.is_full || registering}
                onClick={onBeginRegister}
                className={`w-full px-4 py-2 rounded ${event.is_full || registering ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              >
                {event.is_full
                  ? 'Event Full'
                  : registering
                  ? 'Processing...'
                  : !user
                  ? 'Sign In to Register'
                  : userRegistrations.length > 0 && associateMembers.length > 0
                  ? 'Register Another Person'
                  : (registrationPrice === 0 ? 'Register for Free' : 'Register Now')}
              </button>
            )}
          </div>
        </div>
      </div>

      {paymentModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <EventPaymentForm
              event={event}
              amount={registrationPrice}
              onSuccess={onPaymentSuccess}
              onError={onPaymentError}
              onClose={() => setPaymentModalOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default EventDetail;
